// Ctrl/Cmd+K (PRD §5.2): jump to any region, city, datacenter, rack, device,
// link, monitor, probe or open incident. Searches the lists the UI already
// holds (docs/adr/2026-09-28-3d-topology.md), at most 8 hits per group.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Cable, Gauge, MapPin, RadioTower, Siren } from "lucide-react";

import { useListIncidents, useListLinks, useListMonitors, useListNodes, useListProbes, type MonitorStatus } from "@/api/generated";
import { StatusDot } from "@/components/status/StatusBadge";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { typeLabel } from "@/components/topology/forms";
import { monitorTarget, isInternalRole, useRole, useStateMap } from "@/lib/lookups";
import { SEVERITY } from "@/lib/status";
import { ancestry, childrenOf, encodeView, levelForLink, viewForNode } from "@/lib/topology";
import { useUI } from "@/store/ui";
import { NAV } from "./nav";

const LIMIT = 8;

function matches(needle: string, ...fields: Array<string | null | undefined>): boolean {
  return fields.some((f) => f?.toLowerCase().includes(needle));
}

export function CommandPalette() {
  const open = useUI((s) => s.paletteOpen);
  const setOpen = useUI((s) => s.setPalette);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const role = useRole();
  const internal = isInternalRole(role);

  // The query is cleared on close, so the palette always opens empty.
  const close = () => {
    setOpen(false);
    setQ("");
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (useUI.getState().paletteOpen) {
          setOpen(false);
          setQ("");
        } else setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  // Customers search only what they may see (docs/adr/2026-09-28-customer-tenancy.md).
  const nodesQ = useListNodes(undefined, { query: { enabled: open && internal } });
  const linksQ = useListLinks(undefined, { query: { enabled: open } });
  const monitorsQ = useListMonitors(undefined, { query: { enabled: open } });
  const probesQ = useListProbes(undefined, { query: { enabled: open && internal } });
  const incidentsQ = useListIncidents(undefined, { query: { enabled: open && internal } });
  const states = useStateMap();

  const nodes = useMemo(() => new Map((nodesQ.data ?? []).map((n) => [n.id, n])), [nodesQ.data]);
  const kids = useMemo(() => childrenOf(nodes), [nodes]);
  const needle = q.trim().toLowerCase();

  const results = useMemo(() => {
    const path = (id: string) => ancestry(nodes, id).slice(0, -1).map((x) => nodes.get(x)!.name).join(" › ");
    const incidents = (incidentsQ.data ?? []).filter((i) => !needle || matches(needle, i.title, i.reason)).sort((a, b) => SEVERITY[b.severity].rank - SEVERITY[a.severity].rank);
    return {
      incidents: incidents.slice(0, LIMIT),
      nodes: needle ? [...nodes.values()].filter((n) => matches(needle, n.name, n.slug, n.external_ref)).slice(0, LIMIT).map((n) => ({ n, path: path(n.id) })) : [],
      links: needle ? (linksQ.data ?? []).filter((l) => matches(needle, l.name, l.circuit_id, l.provider, l.external_ref)).slice(0, LIMIT) : [],
      monitors: needle ? (monitorsQ.data ?? []).filter((m) => matches(needle, m.name, m.target_host, ...m.labels)).slice(0, LIMIT) : [],
      probes: needle ? (probesQ.data ?? []).filter((p) => matches(needle, p.name, ...p.labels)).slice(0, LIMIT) : [],
      pages: NAV.filter((n) => role && n.roles.includes(role) && (!needle || matches(needle, n.label))),
    };
  }, [needle, nodes, linksQ.data, monitorsQ.data, probesQ.data, incidentsQ.data, role]);

  const go = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())} title="Go to" description="Search places, links, monitors, probes and open incidents">
      <Command shouldFilter={false}>
        <CommandInput value={q} onValueChange={setQ} placeholder="Search places, links, monitors, probes, incidents…" />
        <CommandList className="max-h-[60vh]">
          <CommandEmpty>Nothing matches.</CommandEmpty>
          {results.incidents.length > 0 && (
            <CommandGroup heading="Open incidents">
              {results.incidents.map((i) => (
                <CommandItem key={i.id} value={`incident-${i.id}`} onSelect={() => go(() => navigate({ to: "/incidents/$incidentId", params: { incidentId: i.id } }))}>
                  <Siren className="text-status-down" />
                  <span className="truncate">{i.title}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{SEVERITY[i.severity].label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.nodes.length > 0 && (
            <CommandGroup heading="Places">
              {results.nodes.map(({ n, path }) => (
                <CommandItem key={n.id} value={`node-${n.id}`} onSelect={() => go(() => navigate({ to: "/topology", search: { at: encodeView(nodes, viewForNode(nodes, n.id)) } }))}>
                  <MapPin />
                  <span className="truncate">{n.name}</span>
                  <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground">{typeLabel(n.type)}{path && ` · ${path}`}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.links.length > 0 && (
            <CommandGroup heading="Links">
              {results.links.map((l) => (
                <CommandItem key={l.id} value={`link-${l.id}`} onSelect={() => go(() => {
                  if (!internal) {
                    navigate({ to: "/links" });
                    return;
                  }
                  const { level, focus } = levelForLink(l, nodes, kids);
                  navigate({ to: "/topology", search: { at: encodeView(nodes, { level, focus, selected: null }), link: l.id } });
                })}>
                  <Cable />
                  <span className="truncate">{l.name}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{typeLabel(l.link_type)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.monitors.length > 0 && (
            <CommandGroup heading="Monitors">
              {results.monitors.map((m) => (
                <CommandItem key={m.id} value={`monitor-${m.id}`} onSelect={() => go(() => navigate({ to: "/monitors/$monitorId", params: { monitorId: m.id } }))}>
                  <StatusDot status={(states.get(m.id)?.status ?? "UNKNOWN") as MonitorStatus} />
                  <span className="truncate">{m.name}</span>
                  <span className="ml-auto shrink-0 truncate font-mono text-xs text-muted-foreground">{m.check_type} {monitorTarget(m)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.probes.length > 0 && (
            <CommandGroup heading="Probes">
              {results.probes.map((p) => (
                <CommandItem key={p.id} value={`probe-${p.id}`} onSelect={() => go(() => navigate({ to: "/probes/$probeId", params: { probeId: p.id } }))}>
                  <RadioTower />
                  <span className="truncate">{p.name}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{p.state.status === "ONLINE" ? "online" : "offline"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.pages.length > 0 && (
            <CommandGroup heading="Pages">
              {results.pages.map((n) => {
                const Icon = n.icon ?? Gauge;
                return (
                  <CommandItem key={n.to + n.label} value={`page-${n.to}-${n.label}`} onSelect={() => go(() => navigate({ to: n.to }))}>
                    <Icon />
                    {n.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
